import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewContainerRef,
  inject,
} from '@angular/core';
import { Observable } from 'rxjs';
import { UntypedFormControl } from '@angular/forms';
import { debounceTime } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { PopupComponent } from '@shared/components/popup/base/popup.component';
import { get } from 'lodash';

/** Matches `.select-menu-inner { max-height: 192px }` — 4 rows of 48px. */
const MAX_VISIBLE_OPTIONS = 4;

@UntilDestroy()
@Component({
  template: '',
  standalone: false,
})
export class SearchPopupHandlerComponent
  extends PopupComponent
  implements OnInit, OnDestroy, OnChanges
{
  protected translate = inject(TranslateService);

  @Input() labelKey?: string;

  @Input() idKey: string | null = 'id';

  @Input() valueKey?: string | null;

  @Input() loading$?: Observable<boolean>;

  @Input() options: any[] = [];

  @Input() model: any;

  @Input() optionTpl?: TemplateRef<any>;

  @Input() originTpl?: TemplateRef<any>;

  @Input() mapValueTo?: (value: any) => any;

  @Input() displayWith?: (value: any) => string;

  @Output() selectChange = new EventEmitter();

  searchControl = new UntypedFormControl();

  /** Rows the dropdown is tall enough to show at once; the rest is virtual-scrolled. */
  visibleOptions = MAX_VISIBLE_OPTIONS;

  protected originalOptions: any[] = [];

  get label(): any {
    return this.displayValue(this.model);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['options']) {
      this.originalOptions = [...this.options];
      requestAnimationFrame(
        () => (this.visibleOptions = Math.min(this.options.length || 1, MAX_VISIBLE_OPTIONS)),
      );
    }
  }

  ngOnInit(): void {
    if (this.model) {
      this.initModel();
    }
    this.originalOptions = [...this.options];
    requestAnimationFrame(
      () => (this.visibleOptions = Math.min(this.options.length || 1, MAX_VISIBLE_OPTIONS)),
    );

    this.searchControl.valueChanges
      .pipe(debounceTime(300), untilDestroyed(this))
      .subscribe((term) => this.search(term));
  }

  search(value: string): void {
    this.options = this.originalOptions.filter((option) => {
      const terms = value.trim().split(' ') ?? [];
      return terms.every((it) =>
        this.translate
          .instant(this.displayValue(option) ?? '')
          .toLowerCase()
          .includes(it.toLowerCase().trim()),
      );
    });
    requestAnimationFrame(() => {
      this.visibleOptions = Math.min(this.options.length || 1, MAX_VISIBLE_OPTIONS);
      this.cdr.detectChanges();
    });
  }

  displayValue(option: any): any {
    if (!this.labelKey && !this.displayWith) {
      return option;
    }
    if (this.displayWith) {
      return this.displayWith(option);
    }
    return option ? get(option, this.labelKey!) : option;
  }

  optionValue(option: any): any {
    if (this.valueKey) {
      return option ? option[this.valueKey] : option;
    }
    if (this.mapValueTo) {
      return option ? this.mapValueTo(option) : option;
    }
    return option;
  }

  optionId(option: any): any {
    if (!this.idKey) {
      return option;
    }
    return option ? option[this.idKey] : option;
  }

  override close() {
    super.close();
    this.searchControl.patchValue('');
  }

  select(option: any): void {
    if (!this.isActive(option)) {
      this.model = option;
      this.selectChange.emit(this.optionValue(option));
      this.onChanged(this.optionValue(option));
    }
    this.close();
  }

  isActive(option: any): boolean {
    if (!this.model) {
      return false;
    }
    return this.optionValue(option) === this.optionValue(this.model);
  }

  override open(dropdownTpl: TemplateRef<any>, origin: HTMLElement) {
    requestAnimationFrame(
      () => (this.visibleOptions = Math.min(this.options.length || 1, MAX_VISIBLE_OPTIONS)),
    );
    super.open(dropdownTpl, origin);
  }

  override writeValue(obj: any): void {
    if (obj == null) {
      return;
    }
    this.initModel();
    this.select(obj);
  }

  private initModel(): void {
    if (!this.idKey) {
      return;
    }
    this.model =
      this.options.find((currentOption) =>
        this.idKey
          ? currentOption[this.idKey] === this.optionId(this.model)
          : currentOption === this.model,
      ) ??
      this.model ??
      null;
  }
}
